import React, { useEffect, useMemo, useState } from "react";
import {
  VStack,
  Text,
  Checkbox,
  Tooltip,
  Box,
  Flex,
  InputGroup,
  InputLeftElement,
  Input,
  FormLabel,
  NumberInput,
  NumberInputField,
} from "@chakra-ui/react";
import GaraazAlertDialogue from "../../../components/GaraazAlertDialogue";
import { isInvalidDiscountAndCredit } from "../../../utils/util";

export type VirtualInvoiceRegenerateTradingParams = {
  orderDiscount: string;
  creditNotePercentage: string;
};

interface RegenerateInvoiceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  canRegenerateOrderInvoice: boolean;
  canRegenerateDeliveryInvoice: boolean;
  hasOrderInvoice: boolean;
  hasDeliveryInvoice: boolean;
  onRegenerateOrderInvoice?: (
    tradingParams?: VirtualInvoiceRegenerateTradingParams,
  ) => Promise<void>;
  onRegenerateDeliveryInvoice?: (deliveryCost: number) => Promise<void>;
  currentDeliveryCost?: number;
  minimumDeliveryCost?: number;
  isVirtualInvoiceRegenerationMode?: boolean;
}

const RegenerateInvoiceDialog: React.FC<RegenerateInvoiceDialogProps> = ({
  isOpen,
  onClose,
  canRegenerateOrderInvoice,
  canRegenerateDeliveryInvoice,
  hasOrderInvoice,
  hasDeliveryInvoice,
  onRegenerateOrderInvoice,
  onRegenerateDeliveryInvoice,
  currentDeliveryCost = 0,
  minimumDeliveryCost = 0,
  isVirtualInvoiceRegenerationMode = false,
}) => {
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenerateOrderSelected, setRegenerateOrderSelected] = useState(false);
  const [regenerateDeliverySelected, setRegenerateDeliverySelected] =
    useState(false);
  const [deliveryCostInput, setDeliveryCostInput] = useState(
    currentDeliveryCost > 0 ? String(currentDeliveryCost) : "",
  );
  const [orderDiscountInput, setOrderDiscountInput] = useState("");
  const [creditNotePercentageInput, setCreditNotePercentageInput] =
    useState("");

  useEffect(() => {
    if (!isOpen) return;
    setDeliveryCostInput(
      currentDeliveryCost > 0 ? String(currentDeliveryCost) : "",
    );
  }, [currentDeliveryCost, isOpen]);

  const parsedDeliveryCost = useMemo(() => {
    if (deliveryCostInput.trim() === "") return null;
    const value = Number(deliveryCostInput);
    return Number.isFinite(value) ? value : null;
  }, [deliveryCostInput]);

  const isDeliveryCostValid = useMemo(() => {
    if (!regenerateDeliverySelected) return true;
    if (parsedDeliveryCost === null) return false;
    if (parsedDeliveryCost < 0) return false;
    return parsedDeliveryCost >= minimumDeliveryCost;
  }, [minimumDeliveryCost, parsedDeliveryCost, regenerateDeliverySelected]);

  const deliveryCostValidationMessage = useMemo(() => {
    if (!regenerateDeliverySelected) return "";
    if (parsedDeliveryCost === null) return "Delivery cost is required";
    if (parsedDeliveryCost < 0) return "Delivery cost cannot be negative";
    if (parsedDeliveryCost < minimumDeliveryCost) {
      return `Delivery cost must be at least Rs.${minimumDeliveryCost} for Direct Delivery`;
    }
    return "";
  }, [minimumDeliveryCost, parsedDeliveryCost, regenerateDeliverySelected]);

  const handleClose = () => {
    setRegenerateOrderSelected(false);
    setRegenerateDeliverySelected(false);
    setDeliveryCostInput(
      currentDeliveryCost > 0 ? String(currentDeliveryCost) : "",
    );
    onClose();
  };

  const handleVirtualDiscountOrCreditChange = (
    value: string,
    field: "discount" | "credit",
  ) => {
    const numValue = Number(value);
    if (value === "" || (numValue >= 0 && numValue <= 100)) {
      if (field === "discount") {
        setOrderDiscountInput(value);
      } else {
        setCreditNotePercentageInput(value);
      }
    }
  };

  const isVirtualTradingInputsValid = useMemo(() => {
    if (!isVirtualInvoiceRegenerationMode) return true;
    return !isInvalidDiscountAndCredit(
      +orderDiscountInput,
      +creditNotePercentageInput,
    );
  }, [
    isVirtualInvoiceRegenerationMode,
    orderDiscountInput,
    creditNotePercentageInput,
  ]);

  const handleSubmit = async () => {
    setIsRegenerating(true);
    try {
      const promises: Promise<void>[] = [];
      const shouldRegenerateOrderInvoice = isVirtualInvoiceRegenerationMode
        ? canRegenerateOrderInvoice
        : regenerateOrderSelected;
      const shouldRegenerateDeliveryInvoice = isVirtualInvoiceRegenerationMode
        ? false
        : regenerateDeliverySelected;

      if (shouldRegenerateOrderInvoice && onRegenerateOrderInvoice) {
        if (isVirtualInvoiceRegenerationMode) {
          promises.push(
            onRegenerateOrderInvoice({
              orderDiscount: orderDiscountInput,
              creditNotePercentage: creditNotePercentageInput,
            }),
          );
        } else {
          promises.push(onRegenerateOrderInvoice());
        }
      }
      if (shouldRegenerateDeliveryInvoice && onRegenerateDeliveryInvoice) {
        promises.push(onRegenerateDeliveryInvoice(parsedDeliveryCost ?? 0));
      }
      await Promise.allSettled(promises);
    } catch {
      console.error("Error regenerating invoice(s)");
    } finally {
      setIsRegenerating(false);
      handleClose();
    }
  };

  const handleOpen = () => {
    setRegenerateOrderSelected(false);
    setRegenerateDeliverySelected(false);
    setDeliveryCostInput(
      currentDeliveryCost > 0 ? String(currentDeliveryCost) : "",
    );
  };

  return (
    <GaraazAlertDialogue
      alertBodyText={
        <VStack align="stretch" spacing={4}>
          <Text fontSize="md" color="gray.600">
            This will initiate the invoice generation process in the background.
            It may take a few moments for the new invoice to appear.
          </Text>
          {isVirtualInvoiceRegenerationMode && (
            <>
              <Text fontSize="sm" color="gray.600">
                <b>Note:</b> It'll generate a single consolidated invoice
                containing items from all child <b>Trading</b> orders which are
                in <b>Supplier Ready</b> state.
              </Text>
              <Box>
                <FormLabel
                  marginRight="auto"
                  fontSize={12}
                  fontWeight={600}
                  marginBottom={1}
                >
                  Discount (%)
                </FormLabel>
                <NumberInput
                  width="22%"
                  value={orderDiscountInput}
                  onChange={(value) =>
                    handleVirtualDiscountOrCreditChange(value, "discount")
                  }
                  size="sm"
                  min={0}
                  max={100}
                >
                  <NumberInputField placeholder="Discount %" />
                </NumberInput>
                <Text fontSize="xs" color="gray.500" mt={1} lineHeight="short">
                  Applied to the invoice amount.
                </Text>
                <FormLabel
                  marginRight="auto"
                  fontSize={12}
                  fontWeight={600}
                  marginTop={3}
                  marginBottom={1}
                >
                  Customer credit (%)
                </FormLabel>
                <NumberInput
                  width="22%"
                  value={creditNotePercentageInput}
                  onChange={(value) =>
                    handleVirtualDiscountOrCreditChange(value, "credit")
                  }
                  size="sm"
                  min={0}
                  max={100}
                >
                  <NumberInputField placeholder="Credit %" />
                </NumberInput>
                <Text fontSize="xs" color="gray.500" mt={1} lineHeight="short">
                  A credit note will be created for this percentage of the
                  invoice amount and applied to the invoice.
                </Text>
                {!isVirtualTradingInputsValid && (
                  <Text fontSize="sm" color="red.500" mt={2}>
                    Enter valid discount (0–100%) and customer credit (≥ 0%).
                  </Text>
                )}
              </Box>
            </>
          )}
          {!isVirtualInvoiceRegenerationMode && (
            <>
              <Text fontSize="md" fontWeight="medium">
                Select which invoices to generate:
              </Text>
              <VStack align="stretch" spacing={3}>
                <Tooltip
                  label={
                    hasOrderInvoice ? "Order invoice is already present" : ""
                  }
                  isDisabled={!hasOrderInvoice}
                  placement="top-start"
                >
                  <Box alignSelf="stretch" py={1}>
                    <Flex align="center" minH="24px">
                      <Checkbox
                        size="md"
                        isChecked={regenerateOrderSelected}
                        onChange={(e) =>
                          setRegenerateOrderSelected(e.target.checked)
                        }
                        isDisabled={
                          hasOrderInvoice || !canRegenerateOrderInvoice
                        }
                      >
                        <Text fontSize="md">Order Invoice</Text>
                      </Checkbox>
                    </Flex>
                  </Box>
                </Tooltip>
                <Tooltip
                  label={
                    hasDeliveryInvoice
                      ? "Delivery invoice is already present"
                      : ""
                  }
                  isDisabled={!hasDeliveryInvoice}
                  placement="top-start"
                >
                  <Box alignSelf="stretch" py={1}>
                    <Flex align="center" minH="24px">
                      <Checkbox
                        size="md"
                        isChecked={regenerateDeliverySelected}
                        onChange={(e) =>
                          setRegenerateDeliverySelected(e.target.checked)
                        }
                        isDisabled={
                          hasDeliveryInvoice || !canRegenerateDeliveryInvoice
                        }
                      >
                        <Text fontSize="md">Delivery Invoice</Text>
                      </Checkbox>
                    </Flex>
                    {regenerateDeliverySelected && (
                      <Box
                        mt={3}
                        ml={4}
                        pl={5}
                        borderLeftWidth="1px"
                        borderLeftColor={
                          !isDeliveryCostValid ? "red.200" : "gray.200"
                        }
                      >
                        <Text
                          fontSize="xs"
                          fontWeight="600"
                          color="gray.600"
                          mb={1.5}
                        >
                          Enter Delivery Cost
                        </Text>
                        <InputGroup size="sm" w="104px">
                          <InputLeftElement
                            pointerEvents="none"
                            fontSize="0.85rem"
                            color="gray.500"
                            left="8px"
                            top="50%"
                            transform="translateY(-50%)"
                            h="calc(100% - 2px)"
                            w="24px"
                            display="flex"
                            alignItems="center"
                            justifyContent="flex-start"
                          >
                            <Text
                              fontSize="0.85rem"
                              color="gray.500"
                              lineHeight="normal"
                            >
                              Rs.
                            </Text>
                          </InputLeftElement>
                          <Input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            placeholder="Enter delivery cost"
                            value={deliveryCostInput}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, "");
                              setDeliveryCostInput(value);
                            }}
                            isDisabled={
                              hasDeliveryInvoice ||
                              !canRegenerateDeliveryInvoice
                            }
                            borderColor={
                              !isDeliveryCostValid ? "red.300" : "gray.300"
                            }
                            bg="white"
                            fontSize="0.9rem"
                            lineHeight="normal"
                            pl="32px"
                            pr="8px"
                          />
                        </InputGroup>
                      </Box>
                    )}
                  </Box>
                </Tooltip>
              </VStack>
            </>
          )}
          {deliveryCostValidationMessage && (
            <Text fontSize="sm" color="red.500">
              *{deliveryCostValidationMessage}
            </Text>
          )}
        </VStack>
      }
      alertDialogueHeaderText={
        isVirtualInvoiceRegenerationMode
          ? "Generate Consolidated Order Invoice"
          : "Regenerate Invoice"
      }
      isOpen={isOpen}
      onClose={handleClose}
      onOpen={handleOpen}
      submitBtnText="Confirm"
      isSubmitBtnLoading={isRegenerating}
      isSubmitBtnDisabled={
        isVirtualInvoiceRegenerationMode
          ? !canRegenerateOrderInvoice || !isVirtualTradingInputsValid
          : (!regenerateOrderSelected && !regenerateDeliverySelected) ||
            !isDeliveryCostValid
      }
      size="xl"
      onSubmitBtn={handleSubmit}
    />
  );
};

export default RegenerateInvoiceDialog;
